import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import { Button } from '@/src/renderer/components/ui/Button'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/src/renderer/components/ui/dropdown-menu'
import { QuickNotesFilledIcon, UpcomingIcon } from '@/src/renderer/components/icons'
import { LifeSpaceColorIcon } from '@/src/renderer/components/icons/LifeSpaceColorIcon'
import { AreaIcon } from '@/src/renderer/components/icons/AreaIcon'
import { TaskEntry, TitleRecognitionScope } from '../TaskEntry'
import type { TaskCreationData, TaskEntryActions } from '../TaskEntry'
import './QuickAddCard.scss'

const LIFE_SPACES: LifeSpace[] = ['personal', 'work', 'home']

export interface QuickAddCardProps {
  /** Der Lebensbereich, in dem die Aufgabe landet — er färbt die Karte. */
  lifeSpace: LifeSpace
  onLifeSpaceChange: (lifeSpace: LifeSpace) => void
  /** Enter oder „Anlegen“. Die Hülle speichert und schließt. */
  onSubmit: (data: TaskCreationData) => void
  /** ⌘↵: anlegen, die Karte bleibt für die nächste offen. */
  onSubmitAndContinue: (data: TaskCreationData) => void
  /** ⇧↵ in der Titelzeile: anlegen, schließen und zur Aufgabe springen. */
  onSubmitAndReveal?: (data: TaskCreationData) => void
  /** Anlegen ohne Titel (Enter, „Anlegen“). Ohne: wie „Abbrechen“. Das Fenster wackelt hier. */
  onSubmitEmpty?: () => void
  /** Esc oder „Abbrechen“. Die Hülle verwirft und schließt. */
  onCancel: () => void
  /** Steigt, um die Eingabe zu leeren, ohne die Karte neu aufzubauen. */
  resetKey?: number
  /** Kurzer Hinweis über dem Titel nach ⌘↵ („In Kurz notiert · Persönlich“). */
  notice?: string | null
  /** Für die Hülle: den Titel fokussieren, wenn das Fenster wieder erscheint. */
  actionsRef?: React.RefObject<TaskEntryActions | null>
  /** Das im Hauptfenster offene Projekt — gibt es zwei „Test“, meint `#Test` dieses. */
  preferredProjectId?: string | null
  /** Jede Änderung der Eingabe — die Hülle merkt sich, ob die Karte leer ist. */
  onDraftChange?: (data: TaskCreationData) => void
}

/**
 * **Die Schnelleingabe** (#234, Variante D wie Things) — nur der Inhalt.
 *
 * Oben die Titelzeile der Listen ohne eigene Karte (`TaskEntry placement="quick-add"`), unten eine
 * Leiste mit dem Ziel und „Abbrechen“ / „Anlegen“. Fenster, Dialog, Escape außerhalb der Felder und
 * Speichern gehören der Hülle (`QuickAddWindow`); die Karte meldet nur nach oben.
 *
 * Das Ziel sagt ehrlich, wo die Aufgabe steht: mit `#Projekt` im Titel dort (und der Bereich folgt dem
 * Projekt), mit Wann in **Demnächst**, sonst in **Kurz notiert**.
 *
 * @example
 * <QuickAddCard lifeSpace="work" onLifeSpaceChange={setLifeSpace}
 *   onSubmit={save} onSubmitAndContinue={saveAndContinue} onCancel={discard} resetKey={resetKey} />
 */
export const QuickAddCard: React.FC<QuickAddCardProps> = ({
  lifeSpace, onLifeSpaceChange, onSubmit, onSubmitAndContinue, onSubmitAndReveal, onSubmitEmpty, onCancel, resetKey, notice, actionsRef, preferredProjectId,
  onDraftChange,
}) => {
  const { t } = useTranslation()
  const ownActions = useRef<TaskEntryActions | null>(null)
  const actions = actionsRef ?? ownActions
  const [hasStart, setHasStart] = useState(false)
  const [target, setTarget] = useState<TaskCreationData['target']>()

  const place = target ? target.projectName || t('project.defaultName') : hasStart ? t('upcoming.title') : t('quickNotes.title')
  const PlaceIcon = target ? AreaIcon : hasStart ? UpcomingIcon : QuickNotesFilledIcon
  const shownSpace = target?.lifeSpace ?? lifeSpace

  /*
    **Tab läuft im Kreis** (#278): hinter „Anlegen“ zurück zum Titel, ⇧Tab im Titel zu „Anlegen“. Keine Hülle
    fängt den Fokus — der Web-Dialog ist nicht modal, das Fenster hat keine Falle; er fiele sonst in die App
    dahinter oder ins Leere. Nur diese zwei Ränder: Dazwischen bleibt Tab nativ, auch in Kästen und Menüs.
  */
  const createRef = useRef<HTMLButtonElement>(null)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey) return
    const create = createRef.current
    const fromTitle = e.target instanceof Element && e.target.classList.contains('task-entry__title-input')
    if (!e.shiftKey && e.target === create) {
      e.preventDefault()
      actions.current?.focusTitle()
    } else if (e.shiftKey && fromTitle && create) {
      e.preventDefault()
      create.focus()
    }
  }

  return (
    <div className="quick-add-card" data-space={shownSpace} onKeyDown={handleKeyDown}>
      {notice && (
        <div className="quick-add-card__notice" role="status">
          <Check size={14} aria-hidden />
          {notice}
        </div>
      )}
      <TitleRecognitionScope lifeSpace={lifeSpace} preferredProjectId={preferredProjectId}>
        <TaskEntry
          mode="creating"
          placement="quick-add"
          // Ohne Einfahren: Die Karte steht beim ersten Bild ganz da — sonst meldete sie kalt geöffnet ihre
          // Höhe mitten im Auffahren, und das Fenster erschien zu klein (E2E „auch kalt“, 04.10.2026).
          initialExpanded
          autoFocus
          resetKey={resetKey}
          actionsRef={actions}
          onSubmitCreate={onSubmit}
          onSubmitCreateAndContinue={onSubmitAndContinue}
          onSubmitCreateAndReveal={onSubmitAndReveal}
          onSubmitEmpty={onSubmitEmpty}
          onCancel={onCancel}
          onDraftChange={(data) => { setHasStart(data.startDate != null); setTarget(data.target); onDraftChange?.(data) }}
        />
      </TitleRecognitionScope>
      <div className="quick-add-card__bar">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <WithTooltip content={t('quickAdd.chooseLifeSpace')}>
              <button type="button" className="quick-add-card__destination">
                <PlaceIcon size={15} className="quick-add-card__destination-icon" />
                <span>{place} · {t(`lifeSpaces.${shownSpace}`)}</span>
                <ChevronDown size={13} aria-hidden />
              </button>
            </WithTooltip>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            // Nach der Wahl geht es mit dem Titel weiter, nicht mit dem Knopf.
            onCloseAutoFocus={(e) => { e.preventDefault(); actions.current?.focusTitle() }}
          >
            {LIFE_SPACES.map((space) => (
              <DropdownMenuItem key={space} onSelect={() => onLifeSpaceChange(space)}>
                <span className="context-menu-content__item-icon"><LifeSpaceColorIcon lifeSpace={space} size={16} /></span>
                {t(`lifeSpaces.${space}`)}
                {space === lifeSpace && <Check className="context-menu-content__item-check" aria-hidden />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="quick-add-card__gap" />
        <Button variant="secondary" size="sm" onClick={onCancel}>{t('quickAdd.cancel')}</Button>
        <Button ref={createRef} size="sm" onClick={() => actions.current?.submit()}>{t('quickAdd.create')}</Button>
      </div>
    </div>
  )
}
